// src/pages/SpecialOffers.js

import {
  Box,
  Card,
  CircularProgress,
  Divider,
  Grid,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import { useEffect, useState } from "react";
import Styles from "../../styles/_global/Styles";
import {
  columnCount,
  handleTrackClick,
  processHtmlContent,
  slugify,
} from "../../utils/utils";
import LiquidGlass from "../LiquidGlass";
import AcceptConfirmationDialog from "./SpecialOffers/AcceptConfirmationDialog";
import CallbackModal from "./SpecialOffers/CallbackModal";
import CategoryTabs from "./SpecialOffers/CategoryTabs";
import QuickQuoteModal from "./SpecialOffers/QuickQuoteModal";
import QuoteDialog from "./SpecialOffers/QuoteDialog";
import VendorCard from "./SpecialOffers/VendorCard";

export default function SpecialOffers({ isMapsReady }) {
  const [isLoading, setIsLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [callbackRequests, setCallbackRequests] = useState({});
  const [open, setOpen] = useState(false);
  const [quickQuoteOpen, setQuickQuoteOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [acceptConfirmationOpen, setAcceptConfirmationOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [confirmationIsError, setConfirmationIsError] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [expandedVendors, setExpandedVendors] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("car-insurance");
  const [userBucket, setUserBucket] = useState(null);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [quickQuoteData, setQuickQuoteData] = useState({});
  const [quickQuoteErrors, setQuickQuoteErrors] = useState({});
  const [quoteAllProviders, setQuoteAllProviders] = useState(false);

  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const [userData, setUserData] = useState({
    firstName: storedUser.firstName || "",
    lastName: storedUser.lastName || "",
    contactNumber: storedUser.mobile || "",
    email: storedUser.email || "",
    bucketId: storedUser.bucketId || null,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  useEffect(() => {
    let hash = window.location.hash.substring(1);
    setSelectedCategory(
      hash
        ? slugify(decodeURIComponent(hash)) || "car-insurance"
        : "car-insurance"
    );
    document.title = `${process.env.REACT_APP_NAME} - Special Offers`;
    setUserBucket(userData.bucketId);

    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/vendors`)
      .then((response) => {
        setVendors(response.data);
        response.data.forEach((vendor) => checkCallbackRequest(vendor.id));
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching vendors:", error);
        setIsLoading(false);
      });
  }, [userData]);

  useEffect(() => {
    const handlePop = () => {
      if (open) handleCloseModal();
      else if (quickQuoteOpen) handleCloseQuickQuote();
      else if (confirmationOpen) handleCloseConfirmation();
      else if (acceptConfirmationOpen) handleCloseAcceptConfirmation();
    };
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, [open, quickQuoteOpen, confirmationOpen, acceptConfirmationOpen]);

  const filteredByBucket = vendors.filter((vendor) => {
    const buckets = Array.isArray(vendor.buckets)
      ? vendor.buckets
      : JSON.parse(vendor.buckets || "[]");
    return buckets.includes(userBucket);
  });

  const filteredByCategory = filteredByBucket.filter(
    (vendor) =>
      vendor.category?.name?.toLowerCase() ===
      selectedCategory.replace(/-/g, " ").toLowerCase()
  );

  const grouped = filteredByBucket.reduce((groups, vendor) => {
    const cat = vendor.category?.name || "Uncategorized";
    const slug = slugify(cat);
    if (!groups[slug])
      groups[slug] = {
        vendors: [],
        description: vendor.category?.description || "",
      };
    groups[slug].vendors.push(vendor);
    return groups;
  }, {});

  const categories = Object.keys(grouped);
  const filteredVendors = selectedCategory
    ? {
        [selectedCategory]: grouped[selectedCategory] || {
          vendors: [],
          description: "",
        },
      }
    : {};

  const checkCallbackRequest = (vendorId) => {
    axios
      .get(
        `${process.env.REACT_APP_BACKEND_URL}/api/callback/callback-requests`,
        {
          params: {
            contactNumber: userData.contactNumber,
            email: userData.email,
            vendorId,
            quoteAllProviders,
            selectedVendors,
            category: selectedCategory.replace(/-/g, " "),
          },
        }
      )
      .then((response) =>
        setCallbackRequests((prev) => ({
          ...prev,
          [vendorId]: response.data.recent
            ? {
                createdAt: new Date(response.data.callback.createdAt || ""),
                id: response.data.callback.id,
              }
            : null,
        }))
      )
      .catch((error) =>
        console.error(`Error fetching callback for vendor ${vendorId}:`, error)
      );
  };

  const handleOpenModal = (vendor) => {
    setSelectedVendor(vendor);
    setOpen(true);
    window.history.pushState(null, "", window.location.href);
  };
  const handleCloseModal = () => {
    setOpen(false);
    setSelectedVendor(null);
    setErrors({});
    setLoading(false);
  };

  const handleOpenQuickQuote = (vendor) => {
    setSelectedVendor(vendor);
    const initial = {};
    (vendor.quoteFormFields || []).forEach((f) => {
      initial[f.name] = storedUser[f.name] || "";
    });
    setQuickQuoteData(initial);
    setQuickQuoteOpen(true);
    window.history.pushState(null, "", window.location.href);
  };
  const handleCloseQuickQuote = () => {
    setQuickQuoteOpen(false);
    if (!confirmationOpen) {
      setSelectedVendor(null);
    }
    setQuickQuoteErrors({});
    setLoading(false);
  };

  const handleCloseConfirmation = () => {
    setConfirmationOpen(false);
    setConfirmationMessage("");
    setConfirmationIsError(false);
    // Don't clear selectedVendor here - it's needed for the accept confirmation dialog
  };
  const handleCloseAcceptConfirmation = () => {
    setAcceptConfirmationOpen(false);
    handleCloseConfirmation();
    setSelectedVendor(null); // Clear selectedVendor when fully closing all dialogs
  };
  
  const submitLeadToIwyze = async (contactData) => {
    const payload = {
      firstName: contactData.firstName || "",
      lastName: contactData.lastName || "",
      idNumber: contactData.idNumber || "",
      contactNumber: contactData.contactNumber || "",
      email: contactData.email || "",
      quoteId: contactData.callbackRef || "",
      userUuid: storedUser.uuid,
      userId: storedUser.id
    };
    
    console.log('Backend API Request:', { payload });
    
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/iwyze/submit-lead`, payload);
      console.log('Backend API Response:', response.data);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Backend API Error:', error.response?.data || error.message);
      return { success: false, error: error.message };
    }
  };
  
  const handleAcceptQuote = async (quoteData) => {
    console.log('handleAcceptQuote called with:', { quoteData, selectedVendor: quoteData?.selectedVendor?.slug });
    
    // Update selectedVendor from quoteData if available
    if (quoteData?.selectedVendor) {
      setSelectedVendor(quoteData.selectedVendor);
    }
    
    if (quoteData?.selectedVendor?.slug === 'iwyze' && quoteData?.callbackRef) {
      console.log('Processing iWyze quote acceptance');
      setLoading(true);
      
      const contactData = {
        firstName: quoteData.firstName || userData.firstName,
        lastName: quoteData.lastName || userData.lastName,
        idNumber: quoteData.idNumber || storedUser.IDNumber || storedUser.idNumber,
        contactNumber: quoteData.contactNumber || userData.contactNumber,
        email: quoteData.email || userData.email,
        callbackRef: quoteData.callbackRef
      };
      
      const result = await submitLeadToIwyze(contactData);
      setLoading(false);
      
      if (result.success) {
        setConfirmationOpen(false);
        setAcceptConfirmationOpen(true);
        window.history.pushState(null, "", window.location.href);
      } else {
        const customErrorMessage = 'We experienced a technical error while trying to submit your details. Please get in touch with one of our consultants on WhatsApp to personally assist you with your quote.<br/><br/><a href="https://wapp.to/finatic" target="_blank" onclick="window.parent.postMessage({type: \'closeDialog\'}, \'*\');">Contact us on WhatsApp</a>';
        setConfirmationMessage(customErrorMessage);
        setConfirmationIsError(true);
      }
    } else {
      console.log('Non-iWyze vendor or missing data, proceeding normally');
      setConfirmationOpen(false);
      setAcceptConfirmationOpen(true);
      window.history.pushState(null, "", window.location.href);
    }
  };

  const handleToggleDescription = (vendorId) => {
    setExpandedVendors((prev) => ({
      ...prev,
      [vendorId]: !prev[vendorId],
    }));
  };

  const handleWhatsAppClick = (vendor, quoteRequested, quoteRequestId) => {
    const user = storedUser;
    let baseUrl =
      process.env.REACT_APP_WHATSAPP_URL || "https://wapp.to/finatic";
    if (
      vendor.whatsAppBusinessNumber &&
      vendor.whatsAppBusinessNumber !== "default"
    ) {
      baseUrl = `https://wa.me/${vendor.whatsAppBusinessNumber}`;
    }
    let params = {
      text: vendor.whatsAppRequestMessage || "",
      utm_source: "special_offers",
      utm_medium: "chat_to_us_button",
      utm_campaign: vendor.name || "vendor_campaign",
      utm_platform: "finatic_app",
      utm_vendor_name: vendor.name || "vendor_name",
      utm_vendor_id: vendor.id || "vendor_id",
      utm_category: selectedCategory.replace(/-/g, " "),
      utm_quote_requested: quoteRequested ? "true" : "false",
      utm_quote_request_id: quoteRequestId || "none",
      user,
      whatsAppBusinessNumber: vendor.whatsAppBusinessNumber,
      whatsAppRequestMessage: vendor.whatsAppRequestMessage,
    };
    handleTrackClick("whatsapp_button", params, user);
    delete params.user;
    delete params.whatsAppRequestMessage;
    const query = new URLSearchParams(params).toString();
    window.open(`${baseUrl}?${query}`, "_blank");
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        px: { xs: 1.5, sm: 2 },
        py: { xs: 1.5, sm: 2 },
      }}
    >
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <CircularProgress size={isMobile ? 32 : 40} />
        </Box>
      ) : (
        <>
          {categories.length > 0 ? (
            <>
              <CategoryTabs
                categories={categories}
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
                isMobile={isMobile}
              />
              {Object.entries(filteredVendors).map(
                ([category, { vendors, description }]) => (
                  <Box key={category} sx={{ mt: 2, mb: 3, borderRadius: 1 }}>
                    <LiquidGlass
                      altComponent={<Card />}
                      title={category
                          .split("-")
                          .map((w) => w[0].toUpperCase() + w.slice(1))
                          .join(" ")}
                      icon="pi pi-bookmark"
                      sx={{
                        padding: 1,
                        mb: 2,
                      }}
                      altSx={{
                        backgroundColor: "background.paper",
                        border: { lg: "1px solid #eee" },
                        boxShadow: "none",
                        mb: 2,
                        p: 2
                      }}
                    >
                      <Typography
                        variant="h6"
                        id={category}
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          fontSize: {
                            xs: "1.1rem",
                            sm: "1.2rem",
                            md: "1.25rem",
                          },
                          color: Styles.secondary.main,
                          display: {
                            xs: "none",
                            md: "block",
                          }
                        }}
                      >
                        {category
                          .split("-")
                          .map((w) => w[0].toUpperCase() + w.slice(1))
                          .join(" ")}
                      </Typography>
                      {description && (
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 1.5,
                            lineHeight: 1.4,
                            fontSize: { xs: "0.75rem", sm: "0.8rem" },
                            ...(isMobile
                              ? {
                                  color: "#fff",
                                  textShadow: "0px 1px 2px rgba(0,0,0,0.5)",
                                }
                              : { color: "text.secondary" }),
                          }}
                          dangerouslySetInnerHTML={{
                            __html: processHtmlContent(description),
                          }}
                        />
                      )}
                    </LiquidGlass>
                    <Divider
                      sx={{
                        mb: 2,
                        ...(isMobile
                          ? { borderColor: "rgba(255,255,255,0.5)" }
                          : { borderColor: "grey.200" }),
                      }}
                    />
                    <Grid container spacing={isMobile ? 1.5 : 2}>
                      {vendors.map((vendor) => (
                        <VendorCard
                          key={vendor.id}
                          vendor={vendor}
                          isMobile={isMobile}
                          columnCount={columnCount(isMobile, isTablet)}
                          callbackRequests={callbackRequests}
                          expandedVendors={expandedVendors}
                          category={category}
                          handleToggleDescription={handleToggleDescription}
                          handleOpenModal={handleOpenModal}
                          handleOpenQuickQuote={handleOpenQuickQuote}
                          handleWhatsAppClick={handleWhatsAppClick}
                        />
                      ))}
                    </Grid>
                  </Box>
                )
              )}
            </>
          ) : (
            <Typography
              sx={{
                fontSize: { xs: "0.85rem", sm: "0.9rem" },
                textAlign: "center",
                py: 1.5,
                ...(isMobile
                  ? {
                      color: "#fff",
                      textShadow: "0px 1px 2px rgba(0,0,0,0.5)",
                    }
                  : {}),
              }}
            >
              No available vendors
            </Typography>
          )}

          <CallbackModal
            open={open}
            onClose={handleCloseModal}
            selectedVendor={selectedVendor}
            userData={userData}
            setUserData={setUserData}
            errors={errors}
            setErrors={setErrors}
            loading={loading}
            setLoading={setLoading}
            quoteAllProviders={quoteAllProviders}
            setQuoteAllProviders={setQuoteAllProviders}
            selectedVendors={selectedVendors}
            setSelectedVendors={setSelectedVendors}
            filteredVendorsByCategory={filteredByCategory}
            callbackRequests={callbackRequests}
            setCallbackRequests={setCallbackRequests}
            selectedCategory={selectedCategory}
            storedUser={storedUser}
            setConfirmationMessage={setConfirmationMessage}
            setConfirmationIsError={setConfirmationIsError}
            setConfirmationOpen={setConfirmationOpen}
            isMobile={isMobile}
          />
          <QuickQuoteModal
            open={quickQuoteOpen}
            onClose={handleCloseQuickQuote}
            selectedVendor={selectedVendor}
            quickQuoteData={quickQuoteData}
            setQuickQuoteData={setQuickQuoteData}
            quickQuoteErrors={quickQuoteErrors}
            setQuickQuoteErrors={setQuickQuoteErrors}
            loading={loading}
            setLoading={setLoading}
            setCallbackRequests={setCallbackRequests}
            setConfirmationMessage={setConfirmationMessage}
            setConfirmationIsError={setConfirmationIsError}
            setConfirmationOpen={setConfirmationOpen}
            isMobile={isMobile}
            isMapsReady={isMapsReady}
          />
          <QuoteDialog
            open={confirmationOpen}
            onClose={handleCloseConfirmation}
            confirmationMessage={confirmationMessage}
            confirmationIsError={confirmationIsError}
            selectedVendor={selectedVendor}
            selectedCategory={selectedCategory}
            handleAcceptQuote={handleAcceptQuote}
            loading={loading}
            isMobile={isMobile}
          />
          <AcceptConfirmationDialog
            open={acceptConfirmationOpen}
            onClose={handleCloseAcceptConfirmation}
            selectedVendor={selectedVendor}
            selectedCategory={selectedCategory}
            isMobile={isMobile}
          />
        </>
      )}
    </Box>
  );
}
